import { Box, Stack, Tooltip } from '@mui/material'
import type { TFunction } from 'i18next'
import type { DateTime } from 'luxon'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { breatheSx, settleInSx } from '@/features/prequalification/localControl/utils/motion'
import { getPhaseChipProps, phaseChipTooltip } from '@/features/prequalification/localControl/utils/phasePresentation'
import type { PqPhase } from '@/features/prequalification/types'

export type PhasePip = {
  /** Stable key (the resource id). */
  key: string
  /** The resource's phase for this test; `null` means it has no run yet. */
  phase: PqPhase | null
  /** The run's scheduled start, so a `SCHEDULED` dot can say *when* it starts. */
  startAt?: DateTime | null
}

type PhasePipsProps = {
  pips: PhasePip[]
}

const PIP_COLOR: Record<PqPhase, string> = {
  SCHEDULED: 'primary.main',
  ENQUEUED: 'info.main',
  STARTED: 'warning.main',
  PASSED: 'success.main',
  FAILED: 'error.main',
  CANCELLED: 'text.disabled',
}

function isRunningPhase(phase: PqPhase | null): boolean {
  return phase === 'STARTED' || phase === 'ENQUEUED'
}

/** The dot's phase, spelling out the start time when it is waiting to begin. */
function pipTitle(phase: PqPhase | null, startAt: DateTime | null, t: TFunction): string {
  if (!phase) return t('prequalifications.resource_set.test.not_run')

  return phaseChipTooltip(phase, startAt, t) ?? getPhaseChipProps(phase, t).label
}

/**
 * A row of small dots, one per resource, coloured by that resource's phase
 * (hollow grey when not started). Gives an instant read on how a test fared
 * across the resource set without expanding it.
 *
 * The dots are alive: a running resource breathes, and a dot settles in the
 * instant its resource crosses into PASSED, so the resource set clearing reads as it
 * happens. Dots that are already terminal on first render stay still, so the
 * page never opens with a flourish.
 */
function PhasePips({ pips }: Readonly<PhasePipsProps>) {
  const { t } = useTranslation()

  // Previous phase per pip, so we only celebrate the *crossing* into PASSED,
  // not every render the dot happens to be green for.
  const previousPhases = useRef<Map<string, PqPhase | null>>(new Map())
  useEffect(() => {
    const next = new Map<string, PqPhase | null>()
    for (const { key, phase } of pips) next.set(key, phase)
    previousPhases.current = next
  })

  return (
    <Stack aria-hidden sx={{ flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
      {pips.map(({ key, phase, startAt = null }) => {
        const previous = previousPhases.current.get(key)
        const justPassed = previous !== undefined && previous !== 'PASSED' && phase === 'PASSED'

        return (
          <Tooltip key={key} title={pipTitle(phase, startAt, t)}>
            <Box
              sx={[
                {
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  transition: 'transform 120ms ease',
                  backgroundColor: phase ? PIP_COLOR[phase] : 'transparent',
                  border: phase ? 'none' : '1.5px solid',
                  borderColor: 'divider',
                  '&:hover': { transform: 'scale(1.35)' },
                },
                isRunningPhase(phase) && breatheSx,
                justPassed && settleInSx,
              ]}
            />
          </Tooltip>
        )
      })}
    </Stack>
  )
}

export default PhasePips
